import type { NextApiRequest } from 'next';
import { acceptLanguage } from 'next/dist/server/accept-header';

const i18n = {
  // These are all the locales you want to support in your application.
  // These should generally match (or at least be a subset of) those in Sitecore.
  locales: ['en', 'es-us'],
  // This is the locale that will be used when visiting a non-locale
  // prefixed path e.g. `/styleguide`.
  defaultLocale: 'en',
};

/**
 * Context values describing locale details.
 */
type LocaleContext = {
  /**
   * @param {string} locale Detected Locale
   */
  locale?: string;
  /**
   * @param {string[]} locales Available Locales
   */
  locales?: string[];
  /**
   * @param {string} defaultLocale Default Locale (so, en)
   */
  defaultLocale?: string;
};

/**
 * Options used to control locale slug computation.
 */
interface LocalSlugOptions {
  /**
   * @param {string} locale The locale to parse
   */
  locale?: string;
  /**
   * @param {boolean} ignoreDefault If explicitly set to false then a slug will be returned for the default locale.
   */
  ignoreDefault?: boolean;
  /**
   * @param {boolean} includeTrailingSlash If explicitly false then no slash will be prepended to the slug.
   */
  includeSlash?: boolean;
}

/**
 * Computes the appropriate URL prefix (slug) for a locale.
 * @param {(string | undefined) |  LocalSlugOptions} input Options used to controle locale generation.
 * @returns {string} A locale slug.
 */
export const getLocaleSlug = (input: (string | undefined) | LocalSlugOptions): string => {
  // If internationalization is not enabled nothing to do.
  if (!i18n) {
    return '';
  }
  // Normalize input
  const props =
    typeof input === 'string' || typeof input === 'undefined' ? { locale: input } : input;
  const normalizedLocale = props.locale?.toLocaleLowerCase() ?? i18n.defaultLocale;
  // Determins if we were passed the default locale
  const isDefaultLocale =
    typeof normalizedLocale === 'undefined' || normalizedLocale === i18n.defaultLocale;
  // If so no slug should be returned unless ignoreDefault is explicitly false
  if (isDefaultLocale && props.ignoreDefault !== false) {
    return '';
  }
  // Otherwise, return locale and optional trailing slash
  return props.includeSlash !== false ? `/${normalizedLocale}` : normalizedLocale;
};

/**
 * Computes locale context for a given request.
 * @param {NextApiRequest} req Request to retrieve locale details from.
 * @returns {LocaleContext} An object containing i8n properties.
 */
export const getLocaleContextForRequest = ({
  cookies,
  headers,
}: Pick<NextApiRequest, 'cookies' | 'headers'>): LocaleContext => {
  if (!i18n) {
    return {};
  }
  const chosenLocale = i18n.locales.find((locale) => locale === cookies.NEXT_LOCALE);
  const detectedLocale = chosenLocale ?? acceptLanguage(headers['accept-language'], i18n.locales);
  return {
    locale: detectedLocale ?? i18n.defaultLocale,
    locales: i18n.locales,
    defaultLocale: i18n.defaultLocale,
  };
};

/**
 * Computes active localefor a given request.
 * @param {NextApiRequest} req Request to retrieve locale details from.
 * @returns {string | undefined} active locale, or undefined if i8n support is not enabled.
 */
export const getLocaleForRequest = (req: NextApiRequest): string | undefined => {
  const { locale } = getLocaleContextForRequest(req);
  return locale;
};
